import { Injectable } from '@angular/core';
import { LoggingService } from '../../logging/logic/logging.service';
import { ActionHandler, ActionHandlerType } from '../action-handler';
import { ClientStateService } from '../../client-state/logic/client-state.service';
import { ServicejobsService } from '../../servicejobs/logic/servicejobs.service';
import { PlaceType } from 'src/app/_model/placetype';
import { NavigateDoActionHandler } from './navigate-do-action-handler.service';
import { NavigateWoActionHandler } from './navigate-wo-action-handler.service';
import { BranchesService } from '../../branches/logic/branches.service';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '../../alert/alert.service';
import { AppParameterService } from '../../app-parameter/logic/app-parameter.service';
import { NavController } from '@ionic/angular';
import {
  NavigationOption,
  NavigationService,
} from '../../navigation/navigation.service';
import { TourStopsService } from '../../tour-stop/logic/tour-stop.service';

@Injectable({
  providedIn: 'root',
})
export class StartNavigateActionHandler implements ActionHandler {
  // Public Member
  public readonly name: ActionHandlerType = 'start-navigate';

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private translate: TranslateService,
    private clientStateService: ClientStateService,
    private servicejobService: ServicejobsService,
    private branchService: BranchesService,
    private navigateDoActionHandler: NavigateDoActionHandler,
    private navigateWoActionHandler: NavigateWoActionHandler,
    private alertService: AlertService,
    private appParameterService: AppParameterService,
    private navigationController: NavController,
    private navigationService: NavigationService,
    private tourStopsService: TourStopsService
  ) {}

  // ActionHandler
  public async executeAction(params?: any): Promise<any> {
    this.logger.log(this.constructor.name, 'Start navigate triggered');
    let clientState = this.clientStateService.theState.getValue();
    let currentSjobId = clientState.serviceJobId;
    let targetSjobId = clientState.targetServiceJobId;
    let currentPt = clientState.place;
    let targetPt = clientState.targetPlace;
    let currentBranchId = clientState.branchId;
    let targetBranchId = clientState.targetBranchId;
    let tourStopId = clientState.tourStopId;

    let sjobId = targetSjobId ? targetSjobId : currentSjobId;
    let pt = targetPt ? targetPt : currentPt;
    let branchId = targetBranchId ? targetBranchId : currentBranchId;

    if (sjobId) {
      this.logger.log(this.constructor.name, 'servicejob available', sjobId);
      let sjob = this.servicejobService.getServiceJobById(sjobId);

      this.doValidationChecksAndStartNavigate(
        sjob.geodataismanual,
        sjob.geodataisaddresspreciseormanual,
        sjob.displayLabel,
        [sjob.validlatitude, sjob.validlongitude],
        sjob.pcdecity + ', ' + sjob.fullstreet
      );
    } else if (pt) {
      this.logger.log(this.constructor.name, 'placetype available', pt);

      if (pt.placeType === PlaceType.EMP_WORKPLACE) {
        await this.navigateDoActionHandler.executeAction(params);
      } else if (pt.placeType === PlaceType.EMP_HOME) {
        await this.navigateWoActionHandler.executeAction(params);
      }
    } else if (tourStopId) {
      this.logger.log(this.constructor.name, 'tourstop available', tourStopId);
      let tourStop = this.tourStopsService.getTourStopById(tourStopId);

      this.logger.log(this.constructor.name, "TourStopp has", tourStop.branchIds?.length, "branches - use first one as target")

      let branch = this.branchService.getBranchById(tourStop.branchIds[0]);
      this.logger.log(this.constructor.name, 'navigate to branch', branch);

      this.doValidationChecksAndStartNavigate(
        branch.geodataismanual,
        branch.geodataisaddresspreciseormanual,
        branch.displayLabel,
        [branch.validlatitude, branch.validlongitude],
        branch.location.label
      );
    } else if (branchId) {
      this.logger.log(this.constructor.name, 'branch available', branchId);
      let branch = this.branchService.getBranchById(branchId);
      this.logger.log(this.constructor.name, 'navigate to branch', branch);

      this.doValidationChecksAndStartNavigate(
        branch.geodataismanual,
        branch.geodataisaddresspreciseormanual,
        branch.displayLabel,
        [branch.validlatitude, branch.validlongitude],
        branch.location.label
      );
    } else {
      this.logger.log(
        this.constructor.name,
        'Neither a sjob nor a placetype are available as target'
      );
    }
  }

  protected async doValidationChecksAndStartNavigate(
    geodataismanual: boolean,
    geodataisaddresspreciseormanual: boolean,
    displayName: string,
    latLng: number[],
    navigationAddress: string
  ) {
    const appParams = this.appParameterService.appParameters$.getValue();
    const showGeodataNotPreciseOrManual = appParams.getBooleanParameter(
      'imsmc.navigate.alert-geodata-not-precise-or-manual',
      true
    );
    const showGeodataNotManual = appParams.getBooleanParameter(
      'imsmc.navigate.alert-geodata-not-manual',
      false
    );

    if (!geodataisaddresspreciseormanual && showGeodataNotPreciseOrManual) {
      this.showGeodataNotPreciseOrManualAlert(displayName, navigationAddress);
    } else if (!geodataismanual && showGeodataNotManual) {
      this.showGeodataNotManualAlert(displayName, latLng);
    } else if (geodataisaddresspreciseormanual) {
      // NAVI LATLON
      this.startNavigate(displayName, latLng);
    } else {
      // NAVI ADDRESS
      this.startNavigate(displayName, navigationAddress);
    }
  }

  protected async showGeodataNotManualAlert(
    displayName: string,
    navigationTarget: number[]
  ) {
    const title = this.translate.instant(
      'ACTION-HANDLER.NAVIGATIONS.POSSIBLY-INPRECISE-GEODATA.TITLE'
    );
    const message = this.translate.instant(
      'ACTION-HANDLER.NAVIGATIONS.POSSIBLY-INPRECISE-GEODATA.MESSAGE'
    );
    const cancel = this.translate.instant(
      'ACTION-HANDLER.NAVIGATIONS.POSSIBLY-INPRECISE-GEODATA.CANCEL'
    );
    const confirm = this.translate.instant(
      'ACTION-HANDLER.NAVIGATIONS.POSSIBLY-INPRECISE-GEODATA.CONFIRM'
    );
    this.alertService.showAlert(
      title,
      displayName,
      message,
      confirm,
      () => {
        this.startNavigate(displayName, navigationTarget);
      },
      () => {
        this.navigationController.navigateForward(`/feature/map`);
      },
      undefined,
      true,
      undefined,
      cancel
    );
  }

  protected async showGeodataNotPreciseOrManualAlert(
    displayName: string,
    navigationTarget: string
  ) {
    const title = this.translate.instant(
      'ACTION-HANDLER.NAVIGATIONS.INPRECISE-GEODATA.TITLE'
    );
    const message = this.translate.instant(
      'ACTION-HANDLER.NAVIGATIONS.INPRECISE-GEODATA.MESSAGE'
    );
    const cancel = this.translate.instant(
      'ACTION-HANDLER.NAVIGATIONS.INPRECISE-GEODATA.CANCEL'
    );
    const confirm = this.translate.instant(
      'ACTION-HANDLER.NAVIGATIONS.INPRECISE-GEODATA.CONFIRM'
    );
    this.alertService.showAlert(
      title,
      displayName,
      message,
      confirm,
      () => {
        this.startNavigate(displayName, navigationTarget);
      },
      () => {
        this.navigationController.navigateForward(`/feature/map`);
      },
      undefined,
      true,
      undefined,
      cancel
    );
  }

  protected async startNavigate(
    displayName: string,
    destination: string | number[]
  ) {
    if (
      (typeof destination === 'string' &&
        (destination === undefined ||
          destination === null ||
          destination === '')) ||
      (Array.isArray(destination) &&
        (destination.includes(undefined) || destination.includes(null)))
    ) {
      const title = this.translate.instant(
        'ACTION-HANDLER.NAVIGATIONS.NO-ADDRESS.TITLE'
      );
      const message = this.translate.instant(
        'ACTION-HANDLER.NAVIGATIONS.NO-ADDRESS.MESSAGE'
      );
      const cancel = this.translate.instant(
        'ACTION-HANDLER.NAVIGATIONS.NO-ADDRESS.CANCEL'
      );
      this.alertService.showAlert(
        title,
        displayName,
        message,
        undefined,
        undefined,
        undefined,
        undefined,
        true,
        undefined,
        cancel
      );
      return;
    }

    const target: NavigationOption = {};
    target.name = displayName;
    if (typeof destination === 'string') {
      target.addr = destination;
    } else {
      target.coords = {
        latitude: destination[0],
        longitude: destination[1],
      };
    }
    this.navigationService.navigateTo(target);
  }
}
